import * as React from 'react';
import * as _ from 'underscore';

import BooleanField from '../BooleanField';

interface IRow {
    elements?: any[];
    onClick?: any[];
}

export interface ITableColumn {
    className: string;
    title: string;
    sort?: boolean;
    sortFunc?(a, b): void;
    orderFirst?: boolean; //VR: true=column to first sorting
}

export interface ITableValue {
    [id: string]: boolean;
}

export interface ITableOptions {
    value: ITableValue;
    values: string[];
    valuesHash?: { [key: string]: any };
    displayKey?: string;
    singleSelectMode?: boolean;
    style?: React.CSSProperties;
    onChange(value: ITableValue): void;
}

function getDefaultSorting(columns: ITableColumn[]) {
    let sortCol = _.find(columns, (obj) => {
        return obj.sort === true && obj.orderFirst === true;
    });

    if (sortCol == null) {
        sortCol = _.find(columns, (obj) => {
            return obj.sort === true;
        });
    }

    return {
        sortIndex: sortCol && _.indexOf(columns, sortCol),
        sortOrder: 1
    };
}

export function Table(props: {
    columns: ITableColumn[];
    // headerContent?: any[];
    // rows?: IRow[];
    options: ITableOptions;

    getRowClickHandlers?(key, onClick);
    getRowContent?(key);
    globalClick?();
}) {
    const [tableValue, setTableValue] = React.useState<ITableValue>(props.options.value || {});
    const [sorting, setSorting] = React.useState<{
        sortIndex: number;
        sortOrder: number;
    }>(getDefaultSorting(props.columns));

    React.useEffect(() => {
        setTableValue(props.options.value || {});
    }, [props.options.value]);

    React.useEffect(() => {
        setSorting(getDefaultSorting(props.columns));
    }, [props.columns]);

    const getSortClickHandler = (obj, i) => {
        return (e) => {
            if (props.options.singleSelectMode !== true) {
                e.stopPropagation();
                e.preventDefault();
            }

            let order;
            let index = i;

            if (sorting.sortIndex !== i) {
                order = 1;
            } else if (sorting.sortOrder === 1) {
                order = -1;
            } else {
                index = null;
            }

            setSorting({
                sortIndex: index,
                sortOrder: order
            });
        };
    };

    const defaultSort = (a, b) => {
        if (a > b) {
            return 1;
        }

        if (b > a) {
            return -1;
        }

        if (a === b) {
            return 0;
        }
    };

    const doSort = (rows: IRow[], index: number, order: number, obj): IRow[] => {
        const sortFunc = obj.sortFunc || defaultSort;

        return rows.slice(0).sort((a, b) => {
            a = a.elements[index];
            b = b.elements[index];

            return sortFunc(a, b) * order;
        });
    };

    const getItemClickHandler = (id) => {
        return (e) => {
            if (props.options.singleSelectMode !== true) {
                e.stopPropagation();
                e.preventDefault();
            }

            let values: ITableValue;

            if (props.options.singleSelectMode) {
                values = {};
                values[id] = true;
            } else {
                values = _.extend({}, tableValue);

                if (values[id] != null) {
                    delete values[id];
                } else {
                    values[id] = true;
                }
            }

            handleOnValueChange(values);

            return false;
        };
    };

    const handleOnValueChange = (value: ITableValue) => {
        setTableValue(value);

        if (typeof props.options.onChange === 'function') {
            props.options.onChange(value);
        }

        if (props.options.singleSelectMode === true && typeof props.globalClick === 'function') {
            props.globalClick();
        }
    };

    const checkAll = () => {
        return _.difference(props.options.values, _.keys(tableValue)).length === 0;
    };

    const handleOnSelectAll = (value: boolean, e) => {
        e.stopPropagation();
        e.preventDefault();

        const all = checkAll();

        let newValue;

        if (all) {
            newValue = {};
        } else {
            newValue = _.reduce(
                props.options.values,
                (res, key: string) => {
                    res[key] = true;

                    return res;
                },
                {}
            );
        }

        handleOnValueChange(newValue);
    };

    const getHeaderContent = () => {
        if (props.options.singleSelectMode !== true) {
            return [<BooleanField value={checkAll()} title={''} onChange={handleOnSelectAll} />];
        } else {
            return null;
        }
    };

    const getOnCheckBoxClickHandler = (onClick: (e) => void) => {
        return (value: boolean, e) => {
            onClick(e);
        };
    };

    const getRow = (key: string): IRow => {
        let display;

        if (props.options != null && props.options.valuesHash[key] != null) {
            display = props.options.valuesHash[key][props.options.displayKey];
        } else {
            display = key;
        }

        const result: IRow = {};
        const onClickHandler = getItemClickHandler(key);

        result.onClick = [null, onClickHandler];

        if (typeof props.getRowClickHandlers === 'function') {
            result.onClick = result.onClick.concat(props.getRowClickHandlers(key, onClickHandler));
        }

        result.elements = [
            <BooleanField
                value={tableValue != null && tableValue[key] === true ? true : false}
                title={''}
                onChange={getOnCheckBoxClickHandler(onClickHandler)}
            />,
            display
        ];

        if (typeof props.getRowContent === 'function') {
            result.elements = result.elements.concat(props.getRowContent(key));
        }

        return result;
    };

    const getRows = (): IRow[] => {
        return _.chain(props.options.values).map(getRow).compact().value();
    };

    // let headerContent = props.headerContent;
    // if (props.headerContent == null) {
    //     headerContent = getHeaderContent();
    // }

    // let rows = props.rows;
    // if (rows == null) {
    //     rows = getRows();
    // }
    const { columns } = props;
    const { sortIndex, sortOrder } = sorting;

    const headerContent = getHeaderContent();
    const rows = getRows();

    const headerElement = _.map(columns, (obj, i) => {
        let onSortClickHandler;
        let sortIcons: React.JSX.Element[];

        if (obj.sort) {
            onSortClickHandler = getSortClickHandler(obj, i);

            sortIcons = [
                <span
                    key={'chevron-down'}
                    className="glyphicon glyphicon-chevron-down sort-icon sort-icon-desc"
                ></span>,
                <span key={'chevron-up'} className="glyphicon glyphicon-chevron-up sort-icon sort-icon-asc"></span>
            ];
        }

        return (
            <th
                key={'th-' + i}
                className={
                    obj.className +
                    (obj.sort ? ' sortable' : '') +
                    (i === sortIndex && sortOrder === 1 ? ' sort-desc' : '') +
                    (i === sortIndex && sortOrder === -1 ? ' sort-asc' : '')
                }
                onClick={onSortClickHandler}
            >
                {headerContent != null && headerContent[i]}
                {obj.title}
                {sortIcons}
            </th>
        );
    });

    let rowsInput: IRow[];

    if (sortIndex != null) {
        rowsInput = doSort(rows, sortIndex, sortOrder, columns[sortIndex]);
    } else {
        rowsInput = rows;
    }

    const rowElements = _.map(rowsInput, (row, y) => {
        const elements = row.elements;
        const onClick = row.onClick;

        const cols = _.map(columns, (obj, i) => {
            return (
                <td key={'td-' + i} className={obj.className} onClick={onClick != null && onClick[i]}>
                    {elements[i]}
                </td>
            );
        });

        return <tr key={'td-' + y}>{cols}</tr>;
    });

    return (
        <div
            className="table-custom-container"
            style={props.options && props.options.style ? props.options.style : null}
        >
            <table className="table-sm w-100 table-bordered table-custom">
                <thead>
                    <tr>{headerElement}</tr>
                </thead>
                <tbody>{rowElements}</tbody>
            </table>
        </div>
    );
}
